// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import { StyledButton } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { color, layoutContainer } from "@stanion/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
    // XNG-Changes
    mainWrapper?: InjectableCss;
    copyrightWrapper?: InjectableCss;
    linksGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
}

export const footerStyles: FooterStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        css: css`
            background-color: ${color("common.ui2")};
            ${({ subscribeHeight }: any) => css`
                margin-top: ${subscribeHeight / 2 + 84}px;
            `}
            border-top: 3px solid ${color("primary")};
            position: relative;
            overflow: initial !important;
        `,
    },
    linksGridItem: {
        width: [6, 3, 3, 2, 2],
    },
    infoGridItem: {
        width: [6, 6, 6, 2, 2],
    },
    mainWrapper: {
        css: css`
            display: flex;
            padding-bottom: 52px;
            ${({ subscribeHeight }: any) => css`
                margin-top: ${subscribeHeight / 2 + 40}px;
            `}
            ${layoutContainer}
        `,
    },
    copyrightWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 20px 0;
            border-top: 1px solid ${color("common.border")};

            & ${TypographyStyle}, & ${StyledButton} ${TypographyStyle} {
                font-size: 12px;
                letter-spacing: 0;
                line-height: 13px;
                margin-bottom: 0;
                color: ${color("text")};
                font-weight: bold;
            }
        `,
    },
};
const styles = footerStyles;

const Footer = ({ id }: PageProps) => {
    const { subscribeHeight } = useAppSelector(state => {
        return {
            subscribeHeight: (state as CustomAppState).xCommon.subscribeHeight,
        };
    });

    const year = new Date().getFullYear();

    return (
        <Page as="footer" isFooter={true} {...styles.page} {...{ subscribeHeight }}>
            <StyledWrapper>
                <XngZone contentId={id} zoneName="Subscribe" />
            </StyledWrapper>
            <StyledWrapper {...styles.mainWrapper} {...{ subscribeHeight }}>
                <GridContainer gap={22}>
                    <GridItem {...styles.linksGridItem}>
                        <XngZone contentId={id} zoneName="linkList1" />
                    </GridItem>
                    <GridItem {...styles.linksGridItem}>
                        <XngZone contentId={id} zoneName="linkList2" />
                    </GridItem>
                    <GridItem {...styles.linksGridItem}>
                        <XngZone contentId={id} zoneName="linkList3" />
                    </GridItem>
                    <GridItem {...styles.linksGridItem}>
                        <XngZone contentId={id} zoneName="linkList4" />
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <XngZone contentId={id} zoneName="ContactUs" />
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <XngZone contentId={id} zoneName="SocialLinks" />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.copyrightWrapper}>
                <Typography>
                    &copy; {year} {siteMessage("Custom_Footer_Copyright")}
                </Typography>
                <XngZone contentId={id} zoneName="CopyrightLinks" normalWidth />
            </StyledWrapper>

            <CookiePrivacyPolicyPopup />
        </Page>
    );
};

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
